import React, { useEffect, useState } from 'react';

import { hasRoles, mergeRoles, CREATE_CLIENT_ROLES, EDIT_CLIENT_ROLES } from '../../../../util/RoleUtil';

import { createClient } from '../../../../services/ClientService';

import Modal from '../../../common/modal/Modal';
import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { Checkbox } from '../../../common/checkbox/Checkbox';
import { Notification } from '../../../notifications/Notification';

import './ClientsCreateConsentTab.css';

const roles = mergeRoles(CREATE_CLIENT_ROLES, EDIT_CLIENT_ROLES);

export const ClientsCreateConsentTab = ({
  client,
  setClient,
  consent,
  setConsent,
  navigateTo,
  navigateToWith,
  setActiveTab,
  handleUpdate,
  context,
  setContext,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  editMode,
}) => {
  useEffect(() => {
    setActiveTab('clientsCreateConsent');

    if (!editMode) {
      if (context.breadcrumbs.newClientConsentBreadcrumb) {
        if (context.breadcrumbs.newClientConsentBreadcrumb.data !== {}) {
          setConsent(context.breadcrumbs.newClientConsentBreadcrumb.data);
        }
      }
    } else {
      if (client) {
        setConsent(client.consents);
      }
    }

    if (Array.isArray(consent)) {
      consent.forEach((consentVO) => {
        switch (consentVO.consentType) {
          case 'researchStudiesConsent':
            setResearchStudiesConsent(consentVO.consentValue || false);
            break;

          case 'clinicalTrialsConsent':
            setClinicalTrialsConsent(consentVO.consentValue || false);
            break;

          case 'researchPartnersShareConsent':
            setResearchPartnersShareConsent(consentVO.consentValue || false);
            break;

          default:
            break;
        }
      });
    }
  }, [client, consent, context.breadcrumbs, editMode, setActiveTab, setConsent]);

  const [researchStudiesConsent, setResearchStudiesConsent] = useState(false);
  const [clinicalTrialsConsent, setClinicalTrialsConsent] = useState(false);
  const [researchPartnersShareConsent, setResearchPartnersShareConsent] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createdClientExternalReference, setCreatedClientExternalReference] = useState('');

  const handleSaveAndNextClick = async () => {
    let clientToSave = getClientToSave();

    let savedClientExternalReference = '';

    const [data, error] = await createClient(context.username, clientToSave);
    if (data) {
      if (data.responseStatus === 'Success') {
        savedClientExternalReference = data.clientExternalReference;
        await clearBreadcrumbs();
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Client Created',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);

    navigateToWith('clientsView', 'clientLocatorInformation', savedClientExternalReference);
  };

  const formatDateString = (inputDateString) => {
    const dateObject = new Date(inputDateString);
    if (isNaN(dateObject.getTime())) {
      return inputDateString;
    }

    const year = dateObject.getFullYear();
    const month = `0${dateObject.getMonth() + 1}`.slice(-2);
    const day = `0${dateObject.getDate()}`.slice(-2);

    return `${year}-${month}-${day}`;
  };

  const handleSaveAndCloseClick = async () => {
    let clientToSave = getClientToSave();

    const [data, error] = await createClient(context.username, clientToSave);
    if (data) {
      if (data.responseStatus === 'Success') {
        setCreatedClientExternalReference(data.clientExternalReference);
        await clearBreadcrumbs();
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Client Created',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
    setIsModalOpen(true);
  };

  const clearBreadcrumbs = async () => {
    setContext((prevState) => {
      return {
        ...prevState,
        breadcrumbs: {
          newClientConsentBreadcrumb: {},
          newClientGeneralBreadcrumb: {},
        },
      };
    });
  };

  const getClientToSave = () => {
    let capturedClient = context.breadcrumbs.newClientGeneralBreadcrumb.data;
    let capturedConsents = [
      {
        consentType: 'researchStudiesConsent',
        consentValue: researchStudiesConsent,
      },
      {
        consentType: 'clinicalTrialsConsent',
        consentValue: clinicalTrialsConsent,
      },
      {
        consentType: 'researchPartnersShareConsent',
        consentValue: researchPartnersShareConsent,
      },
    ];
    return {
      client: {
        title: capturedClient?.title,
        dob: formatDateString(capturedClient.dob),
        firstName: capturedClient.firstName,
        lastName: capturedClient.lastName,
        origin: capturedClient.origin?.value,
        sexAtBirth: capturedClient.sexAtBirth?.value,
        saIdNumber: capturedClient.saIdNumber || null,
        passportNumber: capturedClient.passportNumber || null,
        registrationDate: formatDateString(capturedClient.registrationDate),
        whatShouldWeCallYou: capturedClient.whatShouldWeCallYou,
        consents: capturedConsents.map((capturedConsent) => ({
          consentType: capturedConsent.consentType,
          consentValue: capturedConsent.consentValue,
        })),
      },
    };
  };

  const handleSaveChangesClick = () => {
    let capturedConsents = [
      {
        consentType: 'researchStudiesConsent',
        consentValue: researchStudiesConsent,
      },
      {
        consentType: 'clinicalTrialsConsent',
        consentValue: clinicalTrialsConsent,
      },
      {
        consentType: 'researchPartnersShareConsent',
        consentValue: researchPartnersShareConsent,
      },
    ];

    const updatedConsents = capturedConsents.map((capturedConsent) => {
      const existingConsent = client.consents.find((existing) => existing.consentType === capturedConsent.consentType);
      if (existingConsent) {
        return {
          ...existingConsent,
          consentValue: capturedConsent.consentValue,
        };
      } else {
        return capturedConsent;
      }
    });

    const newClient = {
      ...client,
      consents: updatedConsents,
    };
    setClient(newClient);
    handleUpdate(newClient);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    navigateTo('clientsSearch');
  };

  const fieldsDisabled = !hasRoles(context, roles);

  return (
    <div className="tab-container">
      <div className="consent-section">
        <h3 className={'consent-header'}>
          Consent
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientConsents123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Consent:'}
            />
          )}
        </h3>
        <div className="consent-tab-input-container">
          <p className="consent-toggle-text">
            {'Does the client consent to being contacted with regards to participating in research studies?'}
          </p>
          <Checkbox checked={researchStudiesConsent} onChange={setResearchStudiesConsent} disabled={fieldsDisabled} />
        </div>
        <div className="consent-tab-input-container">
          <p className="consent-toggle-text">
            {'Does the client consent to being contacted with regards to participating in clinical trials?'}
          </p>
          <Checkbox checked={clinicalTrialsConsent} onChange={setClinicalTrialsConsent} disabled={fieldsDisabled} />
        </div>
        <div className="consent-tab-input-container">
          <p className="consent-toggle-text">
            {'Does the client consent to having their personal details shared with research partners?'}
          </p>
          <Checkbox
            checked={researchPartnersShareConsent}
            onChange={setResearchPartnersShareConsent}
            disabled={fieldsDisabled}
          />
        </div>
      </div>

      <div className={'button-container'}>
        {editMode && (
          <div className={'button-create-container'}>
            <SaveButton
              color={'#db602e'}
              title={'Save Changes'}
              onClick={handleSaveChangesClick}
              disabled={fieldsDisabled}
            />
          </div>
        )}
        {!editMode && (
          <div className={'button-edit-container'}>
            <SaveButton
              color={'#ffffff'}
              border={'2px solid #db602e'}
              textColor={'#db602e'}
              title={'Save and Next'}
              onClick={handleSaveAndNextClick}
              disabled={fieldsDisabled}
            />
            <SaveButton
              color={'#db602e'}
              title={'Save And Close'}
              onClick={handleSaveAndCloseClick}
              disabled={fieldsDisabled}
            />
          </div>
        )}
      </div>

      {isModalOpen && (
        <Modal
          closeModal={closeModal}
          setIsModalOpen={setIsModalOpen}
          headerTitle={'Send Notification'}
          hideButton={true}
          width={'650px'}
          height={'250px'}
          justifyContent={'center'}
          contentPadding={'20px'}
        >
          <Notification
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            context={context}
            navigateTo={navigateTo}
            clientExtRef={createdClientExternalReference}
          />
        </Modal>
      )}
    </div>
  );
};
